import React, { useState, useEffect } from 'react';
import { DashboardLayout } from './Dashboard';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  FileText, Download, Eye, Edit, Lock, Search, Share2
} from 'lucide-react';
import { toast } from 'sonner';
import DocumentPreview from '../components/DocumentPreview';
import SignDocumentButton from '../components/SignDocumentButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SharedDocumentsPage = () => {
  const { token } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [previewDocument, setPreviewDocument] = useState(null);

  useEffect(() => {
    const fetchSharedDocuments = async () => {
      try {
        const response = await fetch(`${API_URL}/api/documents/shared`, {
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (response.ok) {
          const data = await response.json();
          setDocuments(data);
        }
      } catch (error) {
        console.error('Failed to fetch shared documents:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSharedDocuments();
  }, [token]);

  const handleDownload = async (doc) => {
    try {
      const response = await fetch(`${API_URL}/api/documents/${doc.document_id}/download`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.original_filename || doc.name;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Document downloaded!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Download failed');
      }
    } catch (error) {
      toast.error('Download failed');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString();
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('pdf')) return '📄';
    if (fileType?.includes('image')) return '🖼️';
    if (fileType?.includes('word') || fileType?.includes('document')) return '📝';
    if (fileType?.includes('excel') || fileType?.includes('spreadsheet')) return '📊';
    return '📁';
  };

  const getPermissionIcon = (permission) => {
    switch (permission) {
      case 'view': return <Eye className="w-4 h-4" />;
      case 'edit': return <Edit className="w-4 h-4" />;
      case 'download': return <Download className="w-4 h-4" />;
      default: return <Eye className="w-4 h-4" />;
    }
  };

  const getPermissionColor = (permission) => {
    switch (permission) {
      case 'view': return 'text-blue-400 bg-blue-400/10';
      case 'edit': return 'text-yellow-400 bg-yellow-400/10';
      case 'download': return 'text-green-400 bg-green-400/10';
      default: return 'text-white/60 bg-white/10';
    }
  };

  const filteredDocuments = documents.filter(doc => 
    doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.owner_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <DashboardLayout title="Shared With Me">
      <div className="space-y-6">
        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <Input
            placeholder="Search shared documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-black/50 border-white/10 text-white placeholder:text-white/30"
            data-testid="search-shared-input"
          />
        </div>

        {/* Info Banner */}
        <div className="bg-secondary/10 border border-secondary/30 rounded-sm p-4 flex items-center gap-3">
          <Share2 className="w-5 h-5 text-secondary" />
          <p className="text-white/80 text-sm">
            These documents have been shared with you by other users. Your access level determines what actions you can perform.
          </p>
        </div>

        {/* Documents Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="animate-pulse bg-black/40 border border-white/10 rounded-sm p-6 h-48" />
            ))}
          </div>
        ) : filteredDocuments.length === 0 ? (
          <div className="text-center py-16">
            <Share2 className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <h3 className="text-xl text-white mb-2">No Shared Documents</h3>
            <p className="text-white/60">
              Documents shared with you will appear here
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredDocuments.map((doc) => (
              <div 
                key={doc.document_id}
                className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-sm p-6 hover:border-secondary/30 transition-all card-hover group"
                data-testid={`shared-document-${doc.document_id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{getFileIcon(doc.file_type)}</span>
                    <div>
                      <h3 className="text-white font-medium truncate max-w-[180px]" title={doc.name}>
                        {doc.name}
                      </h3>
                      <p className="text-white/40 text-xs font-mono">
                        {formatFileSize(doc.file_size)}
                      </p>
                    </div>
                  </div>
                  <span className={`flex items-center gap-1 text-xs px-2 py-1 rounded-sm ${getPermissionColor(doc.permission)}`}>
                    {getPermissionIcon(doc.permission)}
                    {doc.permission}
                  </span>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-xs text-white/60">
                    <span>Shared by:</span>
                    <span className="text-white">{doc.owner_name}</span>
                  </div>
                  
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-white/40">{formatDate(doc.created_at)}</span>
                    <span className="text-primary flex items-center gap-1">
                      <Lock className="w-3 h-3" />
                      Encrypted
                    </span>
                  </div>

                  {/* Preview button - available for view and download permissions */}
                  {(doc.permission === 'view' || doc.permission === 'download' || doc.permission === 'edit') && (
                    <Button
                      onClick={() => setPreviewDocument(doc)}
                      variant="outline"
                      size="sm"
                      className="w-full border-white/10 text-white hover:bg-white/5 mt-2"
                      data-testid={`preview-shared-${doc.document_id}`}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Preview
                    </Button>
                  )}

                  {doc.permission === 'download' && (
                    <Button
                      onClick={() => handleDownload(doc)}
                      variant="outline"
                      size="sm"
                      className="w-full border-white/10 text-white hover:bg-white/5 mt-2"
                      data-testid={`download-shared-${doc.document_id}`}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                  )}

                  {/* Sign button - available for all shared docs so assigned signers can complete fields */}
                  <SignDocumentButton
                    doc={doc}
                    variant="outline"
                    size="sm"
                    className="w-full border-primary/40 text-primary hover:bg-primary/10 mt-2"
                    dataTestId={`sign-shared-${doc.document_id}`}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Document Preview Modal */}
        {previewDocument && (
          <DocumentPreview
            document={previewDocument}
            token={token}
            onClose={() => setPreviewDocument(null)}
            onDownload={handleDownload}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default SharedDocumentsPage;
